import React, { Fragment, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { BriefcaseIcon, CheckCircleIcon, MapPinIcon, TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import moment from "moment";
import Swal from "sweetalert2";
import { estraiMessaggiErrore } from "../services/utils";
import { AuthContext } from "../contexts/auth";
import { getDayCalendarWorkingHour } from "../services/api_calendar";
import { createWorkingAreaTimeSlot, deleteWorkingAreaTimeSlot, getWorkingAreaList, getWorkingAreaTimeSlotList, patchWorkingAreaTimeSlot, bulkUpsertWorkingAreaTimeSlots, bulkDeleteWorkingAreaTimeSlots } from "../services/api_working_area";
import ComboboxAutocomplete from "./combobox_autocomplete";
import CustomTooltipComponent from "./custom_tooltip_component";
import ModalDayHeader from "./modal_day_header";

const formatItalianDate = (date) => {
    if (!date) return "";

    const parsedDate = moment(date, "YYYY-MM-DD", true);

    if (!parsedDate.isValid()) {
        return "";
    }

    const formatted = new Intl.DateTimeFormat("it-IT", {
        weekday: "long",
        day: "2-digit",
        month: "long",
        year: "numeric",
    }).format(
        new Date(`${parsedDate.format("YYYY-MM-DD")}T00:00:00`)
    );

    return (formatted.charAt(0).toUpperCase() + formatted.slice(1));
};

function WorkingAreaAssignmentModal({
    open,
    onClose,
    employee,
    selectedDay,
    permission = null,
    onSave,

    onSwitchToJustification,
    canManageJustifications = false,
    canManageWorkingAreas = false,
    canViewWorkingAreas = false,
}) {
    const { token } = useContext(AuthContext);

    const [calendarDay, setCalendarDay] = useState(null);
    const [workingAreas, setWorkingAreas] = useState([]);
    const [workingAreaAssignments, setWorkingAreaAssignments] = useState([]);

    const [selectedShiftKeys, setSelectedShiftKeys] = useState([]);
    const [bulkWorkingArea, setBulkWorkingArea] = useState(null);

    const [isLoading, setIsLoading] = useState(false);
    const [isBulkSaving, setIsBulkSaving] = useState(false);
    const [savingShiftKeys, setSavingShiftKeys] = useState([]);

    const [error, setError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [currentDay, setCurrentDay] = useState(selectedDay ? moment(selectedDay).format("YYYY-MM-DD") : null);
    
    const controller = useRef(null);

    const employeeId = typeof employee === "object" ? employee?.id : employee;

    const employeeName = useMemo(() => {
        if (!employee || typeof employee !== "object") return "";

        const fullName = [employee.last_name, employee.first_name].filter(Boolean).join(" ").trim();
        return fullName || employee.cf || "";
    }, [employee]);

    const isSenzaCalendario = employee?.shift_type === 'senza_calendario';
    
    useEffect(() => {
        if (!selectedDay) return;

        setCurrentDay(moment(selectedDay).format("YYYY-MM-DD"));
    }, [selectedDay]);

    const normalizedSelectedDay = useMemo(() => {
        if (!currentDay) return null;

        return moment(currentDay, "YYYY-MM-DD", true).format("YYYY-MM-DD");
    }, [currentDay]);

    const normalizeResponseList = (response) => Array.isArray(response) ? response : response?.results || [];

    const handlePreviousDay = () => {
        if (!canGoPrevious || isLoading || isBulkSaving || savingShiftKeys.length > 0) {
            return;
        }
        setCurrentDay((current) => moment(current).subtract(1, "day").format("YYYY-MM-DD"));
    };

    const handleNextDay = () => {
        if (!canGoNext || isLoading || isBulkSaving || savingShiftKeys.length > 0) {
            return;
        }
        setCurrentDay((current) => moment(current).add(1, "day").format("YYYY-MM-DD"));
    };

    const weekStart = useMemo(() => {
        if (!selectedDay) return null;

        return moment(selectedDay).startOf("isoWeek").format("YYYY-MM-DD");
    }, [selectedDay]);

    const weekEnd = useMemo(() => {
        if (!selectedDay) return null;

        return moment(selectedDay).endOf("isoWeek").format("YYYY-MM-DD");
    }, [selectedDay]);

    const canGoPrevious = !!currentDay && !!weekStart && moment(currentDay).isAfter(weekStart, "day");
    const canGoNext = !!currentDay && !!weekEnd && moment(currentDay).isBefore(weekEnd, "day");

    const extractEmployeeDay = (response) => {
        const results = normalizeResponseList(response);

        if (!results.length) return null;

        const employeeResult = results.find((item) => Number(item.id) === Number(employeeId)) || results[0];

        if (!Array.isArray(employeeResult?.days)) return null;

        return employeeResult.days.find(
            (item) => moment(item?.date).format("YYYY-MM-DD") === normalizedSelectedDay,
        ) || null;
    };

    useEffect(() => {
        if (!open || !token || !currentDay || !employeeId) return;

        const fetchModalData = async () => {
            setIsLoading(true);
            setError("");
            setSuccessMessage("");
            setCalendarDay(null);
            setWorkingAreaAssignments([]);
            setSelectedShiftKeys([]);
            setBulkWorkingArea(null);

            controller.current?.abort();
            controller.current = new AbortController();

            try {
                const [calendarResponse, workingAreaResponse, assignmentsResponse] = await Promise.all([
                    getDayCalendarWorkingHour(
                        token,
                        normalizedSelectedDay,
                        null,
                        [Number(employeeId)],
                        1,
                        25,
                        controller.current.signal,
                        permission,
                    ),
                    getWorkingAreaList(token, {
                        status: "active",
                        date: normalizedSelectedDay,
                        paginate: false,
                    }, 'shifts'),
                    getWorkingAreaTimeSlotList(
                        token,
                        {
                            employee: Number(employeeId),
                            date: normalizedSelectedDay,
                            paginate: false,
                        },
                        controller.current.signal,
                        'shifts',
                    ),
                ]);

                setCalendarDay(extractEmployeeDay(calendarResponse));
                setWorkingAreas(normalizeResponseList(workingAreaResponse));
                setWorkingAreaAssignments(normalizeResponseList(assignmentsResponse));
            } catch (requestError) {
                if (requestError?.name !== "CanceledError" && requestError?.name !== "AbortError") {
                    console.error("Errore caricamento dati area di lavoro:", requestError);
                    setError(estraiMessaggiErrore(requestError));
                }
            } finally {
                if (!controller.current?.signal.aborted) setIsLoading(false);
            }
        };

        fetchModalData();

        return () => controller.current?.abort();
    }, [open, token, normalizedSelectedDay, employeeId, permission]);

    const formatTimeValue = (value) => {
        if (value === null || value === undefined || value === "") return "";

        if (typeof value === "number") {
            const hours = Math.floor(value / 60);
            const minutes = value % 60;

            return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
        }

        if (typeof value === "string" && /^\d{1,2}:\d{2}/.test(value)) {
            const [hours, minutes] = value.split(":");

            return `${String(hours).padStart(2, "0")}:${minutes.slice(0, 2)}`;
        }

        const parsedDate = moment(value);

        return parsedDate.isValid() ? parsedDate.format("HH:mm") : String(value);
    };

    const shifts = useMemo(() => {
        if (!calendarDay?.shifts?.length) return [];

        const normalizedShifts = [];

        calendarDay.shifts.forEach((shift, shiftIndex) => {
            const timeSlots = shift.time_slots || [];

            timeSlots.forEach((timeSlot, timeSlotIndex) => {
                const status = shift.status ?? true;

                normalizedShifts.push({
                    id: timeSlot.id,
                    reactKey: `shift-${shiftIndex}-slot-${timeSlot.id ?? timeSlotIndex}`,
                    start_at: formatTimeValue(timeSlot.start_at),
                    end_at: formatTimeValue(timeSlot.end_at),
                    status,
                    is_pending: status === false,
                });
            });
        });

        return normalizedShifts;
    }, [calendarDay]);

    const shiftsWithWorkingArea = useMemo(() => {
        return shifts.map((shift) => {
            const assignment = workingAreaAssignments.find((item) => {
                const timeSlotId = typeof item.time_slot === "object" ? item.time_slot?.id : item.time_slot;

                return (
                    Number(timeSlotId) === Number(shift.id) &&
                    moment(item.date).format("YYYY-MM-DD") === normalizedSelectedDay
                );
            }) || null;

            const workingAreaId = assignment
                ? typeof assignment.working_area === "object"
                    ? assignment.working_area?.id
                    : assignment.working_area
                : null;

            const assignedWorkingArea = assignment
                ? workingAreas.find((area) => Number(area.id) === Number(workingAreaId)) || null
                : null;

            return {
                ...shift,
                workingAreaAssignment: assignment,
                assignedWorkingArea,
            };
        });
    }, [shifts, workingAreaAssignments, workingAreas, normalizedSelectedDay]);

    const isShiftSaving = (shift) => savingShiftKeys.includes(shift.reactKey);

    const addSavingShift = (shift) => {
        setSavingShiftKeys((current) =>
            current.includes(shift.reactKey) ? current : [...current, shift.reactKey],
        );
    };

    const removeSavingShift = (shift) => {
        setSavingShiftKeys((current) => current.filter((key) => key !== shift.reactKey));
    };

    const updateAssignmentState = (savedAssignment) => {
        setWorkingAreaAssignments((current) => {
            const exists = current.some((assignment) => Number(assignment.id) === Number(savedAssignment.id));

            if (exists) {
                return current.map((assignment) =>
                    Number(assignment.id) === Number(savedAssignment.id) ? savedAssignment : assignment,
                );
            }

            return [...current, savedAssignment];
        });
    };

    const saveWorkingAreaForShift = async (shift, workingArea, showMessage = true) => {
        if (!shift || !workingArea) return null;

        if (!calendarDay?.contract_id) {
            throw new Error("Il contratto attivo non è presente nella risposta del calendario");
        }

        if (shift.id === null || shift.id === undefined) {
            throw new Error("Il time slot selezionato non dispone di un identificativo valido");
        }

        const payload = {
            contract: Number(calendarDay.contract_id),
            time_slot: Number(shift.id),
            working_area: Number(workingArea.id),
            date: normalizedSelectedDay,
        };

        addSavingShift(shift);

        try {
            let savedAssignment;

            if (shift.workingAreaAssignment) {
                savedAssignment = await patchWorkingAreaTimeSlot(
                    token,
                    shift.workingAreaAssignment.id,
                    {
                        working_area: payload.working_area,
                    },
                    'shifts',
                );
            } else {
                savedAssignment = await createWorkingAreaTimeSlot(token, payload, 'shifts');
            }

            updateAssignmentState(savedAssignment);

            if (showMessage) {
                setSuccessMessage(
                    shift.workingAreaAssignment
                        ? "Area di lavoro aggiornata correttamente"
                        : "Area di lavoro assegnata correttamente"
                );
            }

            await onSave?.({
                employeeId: Number(employeeId),
                date: normalizedSelectedDay,
            });

            return savedAssignment;
        } finally {
            removeSavingShift(shift);
        }
    };

    useEffect(() => {
        if (!successMessage) return;

        const timeout = setTimeout(() => {
            setSuccessMessage("");
        }, 2500);

        return () => clearTimeout(timeout);
    }, [successMessage]);

    const handleWorkingAreaChange = async (shift, workingArea) => {
        if (!workingArea || isShiftSaving(shift) || isBulkSaving) return;

        setError("");
        setSuccessMessage("");

        try {
            await saveWorkingAreaForShift(shift, workingArea);
        } catch (requestError) {
            console.error("Errore assegnazione area di lavoro:", requestError);

            if (requestError?.response?.data) {
                setError(estraiMessaggiErrore(requestError));
            } else {
                setError(requestError?.message || "Errore durante il salvataggio dell'area di lavoro.");
            }
        }
    };

    const handleRemoveWorkingArea = async (shift) => {
        const assignment = shift.workingAreaAssignment;

        if (!assignment || isShiftSaving(shift) || isBulkSaving) return;

        addSavingShift(shift);
        setError("");
        setSuccessMessage("");

        try {
            await deleteWorkingAreaTimeSlot(token, assignment.id, 'shifts');

            setWorkingAreaAssignments((current) =>
                current.filter((item) => Number(item.id) !== Number(assignment.id))
            );

            setSuccessMessage("Area di lavoro rimossa correttamente");

            await onSave?.({
                employeeId: Number(employeeId),
                date: normalizedSelectedDay,
            });

        } catch (requestError) {
            console.error("Errore rimozione area di lavoro:", requestError);
            setError(estraiMessaggiErrore(requestError));
        } finally {
            removeSavingShift(shift);
        }
    };

    const toggleShiftSelection = (shift) => {
        setSelectedShiftKeys((current) =>
            current.includes(shift.reactKey)
                ? current.filter((key) => key !== shift.reactKey)
                : [...current, shift.reactKey],
        );
    };

    const allShiftsSelected =
        shiftsWithWorkingArea.length > 0 &&
        shiftsWithWorkingArea.every((shift) => selectedShiftKeys.includes(shift.reactKey));

    const handleToggleAll = () => {
        if (allShiftsSelected) {
            setSelectedShiftKeys([]);
            return;
        }

        setSelectedShiftKeys(shiftsWithWorkingArea.map((shift) => shift.reactKey));
    };

    const selectedAssignedShifts = useMemo(() => {
        return shiftsWithWorkingArea.filter(
            (shift) => selectedShiftKeys.includes(shift.reactKey) && shift.workingAreaAssignment,
        );
    }, [shiftsWithWorkingArea, selectedShiftKeys]);

    const selectedAssignedCount = selectedAssignedShifts.length;

    const handleSelectWithoutArea = () => {
        setSelectedShiftKeys(
            shiftsWithWorkingArea
                .filter((shift) => !shift.workingAreaAssignment)
                .map((shift) => shift.reactKey),
        );
    };

    const handleBulkAssignment = async () => {
        if (!bulkWorkingArea || !selectedShiftKeys.length || isBulkSaving) return;

        const selectedShifts = shiftsWithWorkingArea.filter((shift) => selectedShiftKeys.includes(shift.reactKey));

        setIsBulkSaving(true);
        setError("");
        setSuccessMessage("");

        try {
            const response = await bulkUpsertWorkingAreaTimeSlots(token, {
                contract: Number(calendarDay.contract_id),
                working_area: Number(bulkWorkingArea.id),
                date: normalizedSelectedDay,
                time_slots: selectedShifts.map((shift) => Number(shift.id)),
            }, 'shifts');

            const savedAssignments = Array.isArray(response.assignments) ? response.assignments : [];

            setWorkingAreaAssignments((current) => {
                const assignmentsMap = new Map(
                    current.map((assignment) => [Number(assignment.id), assignment]),
                );

                savedAssignments.forEach((assignment) => {
                    assignmentsMap.set(Number(assignment.id), assignment);
                });

                return Array.from(assignmentsMap.values());
            });

            const totalCount =
                Number(response.created_count || 0) +
                Number(response.updated_count || 0) +
                Number(response.unchanged_count || 0);

            setSelectedShiftKeys([]);
            setBulkWorkingArea(null);

            setSuccessMessage(
                `Area di lavoro applicata a ${totalCount} ${
                    totalCount === 1 ? "fascia oraria" : "fasce orarie"
                }`,
            );

            await onSave?.({ employeeId: Number(employeeId), date: normalizedSelectedDay });
        } catch (requestError) {
            console.error("Errore assegnazione massiva:", requestError);
            setError(estraiMessaggiErrore(requestError));
        } finally {
            setIsBulkSaving(false);
        }
    };

    const handleBulkDelete = async () => {
        if (!selectedAssignedCount || isBulkSaving) return;

        const result = await Swal.fire({
            icon: "warning",
            title: "Rimuovere le aree associate?",
            text: `Verranno rimosse le aree da ${selectedAssignedCount} ${
                selectedAssignedCount === 1 ? "fascia oraria" : "fasce orarie"
            }.`,
            showCancelButton: true,
            confirmButtonText: "Rimuovi",
            cancelButtonText: "Annulla",
            confirmButtonColor: "#dc2626",
            reverseButtons: true,
        });

        if (!result.isConfirmed) return;

        setIsBulkSaving(true);
        setError("");
        setSuccessMessage("");

        try {
            const ids = selectedAssignedShifts.map(
                (shift) => shift.workingAreaAssignment.id,
            );

            const response = await bulkDeleteWorkingAreaTimeSlots(token, ids, 'shifts');

            setWorkingAreaAssignments((current) =>
                current.filter(
                    (assignment) =>
                        !response.deleted_ids.includes(assignment.id),
                ),
            );

            setSelectedShiftKeys([]);

            setSuccessMessage(
                `${response.deleted_count} ${
                    response.deleted_count === 1
                        ? "area rimossa"
                        : "aree rimosse"
                } correttamente`,
            );

            await onSave?.({ employeeId: Number(employeeId), date: normalizedSelectedDay });

        } catch (requestError) {
            console.error("Errore rimozione massiva aree di lavoro:", requestError);
            setError(estraiMessaggiErrore(requestError));
        } finally {
            setIsBulkSaving(false);
        }
    };

    const handleClose = () => {
        if (isBulkSaving || savingShiftKeys.length > 0) return;

        controller.current?.abort();
        setCalendarDay(null);
        setWorkingAreas([]);
        setWorkingAreaAssignments([]);
        setSelectedShiftKeys([]);
        setBulkWorkingArea(null);
        setError("");
        setSuccessMessage("");
        onClose();
    };

    useEffect(() => {
        if (!open || !isSenzaCalendario || !currentDay) return;

        onSwitchToJustification?.(
            moment(currentDay).format("YYYY-MM-DD")
        );
    }, [open, isSenzaCalendario, currentDay, onSwitchToJustification]);

    if (isSenzaCalendario) {
        return null;
    }

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="relative z-[200]" onClose={() => {}}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-start justify-center px-4 pb-6 pt-10 sm:px-6">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-4xl overflow-visible rounded-xl border border-gray-200 bg-white text-left shadow-2xl">
                                <ModalDayHeader
                                    title="Assegna aree di lavoro"
                                    subtitle={`${employeeName} · ${formatItalianDate(currentDay)}`}
                                    Icon={MapPinIcon}
                                    mode="working-area"
                                    showWorkingAreaMode={
                                        !isSenzaCalendario &&
                                        (canManageWorkingAreas || canViewWorkingAreas)
                                    }
                                    showJustificationMode={canManageJustifications}
                                    onModeChange={(mode) => {
                                        if (mode === "justification") {
                                            onSwitchToJustification?.(moment(currentDay).format("YYYY-MM-DD"));
                                        }
                                    }}
                                    onClose={handleClose}
                                    onPrevious={handlePreviousDay}
                                    onNext={handleNextDay}
                                    previousDisabled={!canGoPrevious || isLoading || isBulkSaving || savingShiftKeys.length > 0}
                                    nextDisabled={!canGoNext || isLoading || isBulkSaving || savingShiftKeys.length > 0}
                                    closeDisabled={isBulkSaving || savingShiftKeys.length > 0}
                                    isSenzaCalendario={isSenzaCalendario}
                                />

                                <div className="space-y-5 px-5 py-5 sm:px-6 bg-gray-50">
                                    {error && <Alert severity="error">{error}</Alert>}
                                    {successMessage && <Alert severity="success">{successMessage}</Alert>}

                                    {isLoading ? (
                                        <div className="flex min-h-64 items-center justify-center">
                                            <CircularProgress size={32} />
                                        </div>
                                    ) : !calendarDay ? (
                                        <div className="rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 px-5 py-12 text-center">
                                            <BriefcaseIcon className="mx-auto h-8 w-8 text-gray-400" />
                                            <p className="mt-3 text-sm font-semibold text-gray-700">Nessun dato disponibile</p>
                                        </div>
                                    ) : shiftsWithWorkingArea.length === 0 ? (
                                        <div className="rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 px-5 py-12 text-center">
                                            <BriefcaseIcon className="mx-auto h-8 w-8 text-gray-400" />
                                            <p className="mt-3 text-sm font-semibold text-gray-700">Nessuna fascia oraria presente</p>
                                            <p className="mt-1 text-xs text-gray-500">
                                                Per assegnare un’area di lavoro deve essere presente almeno una fascia oraria
                                            </p>
                                        </div>
                                    ) : (
                                        <>
                                            <div>
                                                <div className="mb-3 flex flex-col gap-2">
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <h3 className="mr-2 text-xs font-semibold uppercase tracking-wide text-gray-700">
                                                            Fasce orarie
                                                        </h3>

                                                        <button
                                                            type="button"
                                                            onClick={handleToggleAll}
                                                            className="rounded-md border border-indigo-200 bg-indigo-50 px-3 py-1 text-[11px] font-semibold uppercase text-indigo-700 shadow-sm transition-colors hover:bg-indigo-100"
                                                        >
                                                            {allShiftsSelected ? "Deseleziona tutte" : "Seleziona tutte"}
                                                        </button>

                                                        <button
                                                            type="button"
                                                            onClick={handleSelectWithoutArea}
                                                            className="rounded-md border border-gray-300 bg-white px-3 py-1 text-[11px] font-semibold uppercase text-gray-600 shadow-sm transition-colors hover:bg-gray-100"
                                                        >
                                                            Seleziona senza area
                                                        </button>

                                                        <button
                                                            type="button"
                                                            onClick={handleBulkDelete}
                                                            disabled={!selectedAssignedCount || isBulkSaving}
                                                            className="rounded-md border border-red-200 bg-red-50 px-3 py-1 text-[11px] font-semibold uppercase text-red-700 shadow-sm transition-colors hover:border-red-300 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                                                        >
                                                            Rimuovi aree
                                                            {selectedAssignedCount > 0 && (
                                                                <span className="ml-1">({selectedAssignedCount})</span>
                                                            )}
                                                        </button>
                                                    </div>

                                                    <p className="text-xs text-gray-500">
                                                        Puoi assegnare un'area singolarmente oppure selezionare più fasce e utilizzare l'assegnazione massiva sulle fasce del giorno
                                                    </p>
                                                </div>

                                                <div className="overflow-visible rounded-md border border-gray-200">
                                                    <div className="hidden grid-cols-[23%_37%_32%_8%] items-center border-b border-gray-200 bg-indigo-100/60 px-2 py-2 md:grid">
                                                        <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                                            Fascia oraria
                                                        </div>

                                                        <div className="px-2 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                                            Selezione area
                                                        </div>

                                                        <div className="px-2 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                                            Area assegnata
                                                        </div>

                                                        <div className="text-center text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                                            Azioni
                                                        </div>
                                                    </div>

                                                    <div className="divide-y divide-gray-200">
                                                        {shiftsWithWorkingArea.map((shift) => {
                                                            const isSelected = selectedShiftKeys.includes(shift.reactKey);
                                                            const isPending = shift.status === false || shift.is_pending;
                                                            const isSaving = isShiftSaving(shift);

                                                            return (
                                                                <div
                                                                    key={shift.reactKey}
                                                                    className={`px-2 py-2 transition-colors rounded-b-md ${
                                                                        isSelected
                                                                            ? isPending ? "bg-orange-100" : "bg-indigo-50"
                                                                            : isPending ? "bg-orange-50/70 hover:bg-orange-100/70" : "bg-white hover:bg-indigo-50/40"
                                                                    }`}
                                                                >
                                                                    <div className="grid grid-cols-1 gap-3 md:grid-cols-[23%_37%_32%_8%] md:items-center md:gap-0">
                                                                        <div className="min-w-0 md:pr-2">
                                                                            <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400 md:hidden">
                                                                                Fascia oraria
                                                                            </p>

                                                                            <label className="flex min-w-0 cursor-pointer items-center gap-2">
                                                                                <input
                                                                                    type="checkbox"
                                                                                    checked={isSelected}
                                                                                    onChange={() => toggleShiftSelection(shift)}
                                                                                    disabled={isBulkSaving || isSaving}
                                                                                    className="h-4 w-4 shrink-0 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
                                                                                />

                                                                                {/* <BriefcaseIcon className={`h-3.5 w-3.5 shrink-0 ${isPending ? "text-orange-500" : "text-indigo-500"}`} /> */}

                                                                                <div className="min-w-0">
                                                                                    <p className={`whitespace-nowrap text-sm font-semibold ${isPending ? "text-orange-700" : "text-gray-800"}`}>
                                                                                        {shift.start_at || "--:--"} - {shift.end_at || "--:--"}
                                                                                    </p>

                                                                                </div>
                                                                            </label>
                                                                        </div>

                                                                        <div className="min-w-0 md:px-2">
                                                                            <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400 md:hidden">
                                                                                Selezione area
                                                                            </p>

                                                                            <div className="w-full min-w-0 rounded-md border border-gray-200 bg-white">
                                                                                <ComboboxAutocomplete
                                                                                    options={workingAreas}
                                                                                    bgActiveColor="bg-indigo-100"
                                                                                    textActiveColor="text-gray-900"
                                                                                    placeholder="Seleziona area di lavoro..."
                                                                                    pyClass="py-1.5"
                                                                                    value={shift.assignedWorkingArea}
                                                                                    onChange={(value) => handleWorkingAreaChange(shift, value ?? null)}
                                                                                    displayValue={(area) => area?.name || ""}
                                                                                    renderOption={(area) => (
                                                                                        <div className="flex min-w-0 items-center justify-between gap-3">
                                                                                            <span className="truncate text-sm text-gray-700">{area.name}</span>

                                                                                            {area.justification_code && (
                                                                                                <span className="shrink-0 rounded-md bg-indigo-200 px-2 py-0.5 text-[10px] font-semibold text-indigo-700">
                                                                                                    {area.justification_code}
                                                                                                </span>
                                                                                            )}
                                                                                        </div>
                                                                                    )}
                                                                                    positionOptions="absolute"
                                                                                    zIndex="z-[1200]"
                                                                                    disabled={isSaving || isBulkSaving}
                                                                                    hideXMarkIcon={true}
                                                                                />
                                                                            </div>
                                                                        </div>

                                                                        <div className="min-w-0 md:px-2">
                                                                            <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400 md:hidden">
                                                                                Area assegnata
                                                                            </p>

                                                                            {isSaving ? (
                                                                                <div className="flex min-h-9 items-center gap-2 text-xs font-semibold text-gray-500">
                                                                                    <CircularProgress size={17} />Salvataggio...
                                                                                </div>
                                                                            ) : shift.assignedWorkingArea ? (
                                                                                <div className="min-w-0 grid grid-cols-[1fr_20%] items-center gap-2">
                                                                                    <CustomTooltipComponent title={shift?.assignedWorkingArea?.name || ""} placement="top" arrow>
                                                                                        <span className="block truncate text-xs font-semibold text-indigo-700">
                                                                                            {shift?.assignedWorkingArea?.name}
                                                                                        </span>
                                                                                    </CustomTooltipComponent>

                                                                                    <div className="flex min-w-0 justify-end">
                                                                                        {shift?.assignedWorkingArea?.justification_code ? (
                                                                                            <CustomTooltipComponent
                                                                                                title={shift?.assignedWorkingArea?.justification_description || shift?.assignedWorkingArea?.justification_code}
                                                                                                placement="top"
                                                                                                arrow
                                                                                            >
                                                                                                <span className="inline-flex max-w-full cursor-default justify-center truncate rounded bg-indigo-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-indigo-700">
                                                                                                    {shift.assignedWorkingArea.justification_code}
                                                                                                </span>
                                                                                            </CustomTooltipComponent>
                                                                                        ) : (
                                                                                            <span className="w-full" />
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            ) : (
                                                                                <span className="text-sm font-normal text-gray-400">
                                                                                    Nessuna area
                                                                                </span>
                                                                            )}
                                                                        </div>

                                                                        <div className="flex min-h-9 items-center justify-start md:justify-center">
                                                                            <p className="mr-3 text-[10px] font-semibold uppercase tracking-wide text-gray-400 md:hidden">
                                                                                Azioni
                                                                            </p>

                                                                            {!isSaving && shift.assignedWorkingArea && (
                                                                                <CustomTooltipComponent title="Rimuovi area associata">
                                                                                    <span className="inline-flex">
                                                                                        <button
                                                                                            type="button"
                                                                                            aria-label={`Rimuovi area ${shift.assignedWorkingArea.name}`}
                                                                                            onClick={() => handleRemoveWorkingArea(shift)}
                                                                                            disabled={isBulkSaving}
                                                                                            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-red-50 text-red-500 transition-colors hover:bg-red-100 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                                                                                        >
                                                                                            <TrashIcon className="h-4 w-4" />
                                                                                        </button>
                                                                                    </span>
                                                                                </CustomTooltipComponent>
                                                                            )}

                                                                            {!isSaving && !shift.assignedWorkingArea && (
                                                                                <span className="text-xs text-gray-300">—</span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="border-b border-gray-200 px-2 h-2"></div>

                                            <div className={`${selectedShiftKeys.length ? "border-indigo-200" : "border-gray-200 "}`}>
                                                <div className="mb-2 flex items-center justify-between gap-3">
                                                    <div>
                                                        <h3 className={`text-xs font-semibold uppercase tracking-wide ${selectedShiftKeys.length ? "text-indigo-800" : "text-gray-500"}`}>
                                                            Assegnazione massiva giorno
                                                        </h3>

                                                        <p className="mt-0.5 text-[11px] text-gray-500">
                                                            {selectedShiftKeys.length ? `${selectedShiftKeys.length} ${selectedShiftKeys.length === 1 ? "fascia selezionata" : "fasce selezionate"}`
                                                                : "Seleziona almeno una fascia dalla tabella"}
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
                                                    <div className={`min-w-0 rounded-md border border-gray-200 bg-white ${
                                                        !selectedShiftKeys.length ? "pointer-events-none opacity-60" : ""
                                                    }`}>
                                                        <ComboboxAutocomplete
                                                            options={workingAreas}
                                                            bgActiveColor="bg-indigo-100"
                                                            textActiveColor="text-gray-900"
                                                            pyClass="py-1.5"
                                                            placeholder={selectedShiftKeys.length ? "Seleziona l’area da applicare..." : "Seleziona prima le fasce orarie"}
                                                            value={bulkWorkingArea}
                                                            onChange={(value) => setBulkWorkingArea(value ?? null)}
                                                            displayValue={(area) => area?.name || ""}
                                                            renderOption={(area) => (
                                                                <div className="flex min-w-0 items-center justify-between gap-3">
                                                                    <span className="truncate text-sm text-gray-700">{area.name}</span>

                                                                    {area.justification_code && (
                                                                        <span className="shrink-0 rounded bg-indigo-200 px-1.5 py-0.5 text-[9px] font-semibold text-indigo-700">
                                                                            {area.justification_code}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            )}
                                                            positionOptions="absolute"
                                                            zIndex="z-[1200]"
                                                            disabled={!selectedShiftKeys.length || isBulkSaving}
                                                        />
                                                    </div>

                                                    <button
                                                        type="button"
                                                        onClick={handleBulkAssignment}
                                                        disabled={!bulkWorkingArea || !selectedShiftKeys.length || isBulkSaving}
                                                        className="inline-flex h-9 w-full items-center justify-center gap-2 rounded-md bg-indigo-600 px-4 text-[10px] font-semibold uppercase text-white shadow-sm transition-colors hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                                                    >
                                                        {isBulkSaving && <CircularProgress size={14} color="inherit" />}

                                                        {isBulkSaving
                                                            ? "Salvataggio..."
                                                            : `Applica${selectedShiftKeys.length ? ` (${selectedShiftKeys.length})` : ""}`}
                                                    </button>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>

                                <div className="flex items-center justify-end rounded-b-xl border-t border-gray-200 bg-gray-50/70 px-5 py-4 sm:px-6">
                                    <button
                                        type="button"
                                        onClick={handleClose}
                                        disabled={isBulkSaving || savingShiftKeys.length > 0}
                                        className="rounded-md border border-gray-300 bg-white px-5 py-2 text-xs font-semibold uppercase text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        Chiudi
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}

export default WorkingAreaAssignmentModal;
